import { useState } from "react";
import { SectionLabel } from "@/components/SectionLabel";
import { WordReveal } from "@/components/WordReveal";
import { usePublications, groupByYear } from "@/hooks/usePublications";
import type { Publication } from "@/lib/api/publications";

const INITIAL_PUBLICATION_COUNT = 12;

export function Publications() {
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading, isError } = usePublications();
  const visiblePublications = data
    ? showAll
      ? data
      : data.slice(0, INITIAL_PUBLICATION_COUNT)
    : [];
  const groups = groupByYear(visiblePublications);
  const total = data?.length ?? 0;
  const hiddenCount = Math.max(0, total - visiblePublications.length);

  return (
    <section
      id="publications"
      className="scroll-mt-20 border-t border-line px-6 py-20 md:px-10 lg:px-16"
      aria-labelledby="publications-h"
    >
      <header className="mx-auto mb-10 flex max-w-5xl flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <SectionLabel index={4}>Publikasjoner</SectionLabel>
          <WordReveal
            as="h2"
            id="publications-h"
            className="mt-4 block font-display text-4xl font-normal leading-[1.05] md:text-5xl"
          >
            Publisert forskning
          </WordReveal>
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-muted">
          {showAll || total <= INITIAL_PUBLICATION_COUNT
            ? `${total} bekreftet`
            : `${visiblePublications.length} av ${total} vist`}
          {" · "}
          OpenAlex · ORCID · Semantic Scholar
        </p>
      </header>

      <div className="mx-auto max-w-5xl">
        {isLoading && <p className="text-muted">Laster publikasjoner …</p>}
        {isError && <p className="text-muted">Kunne ikke laste publikasjoner.</p>}

        <ol className="space-y-8">
          {groups.map(([year, items]) => (
            <YearGroup key={year} year={year} items={items} />
          ))}
        </ol>

        {total > INITIAL_PUBLICATION_COUNT ? (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAll((current) => !current)}
              className="border border-line bg-panel px-5 py-3 font-mono text-xs uppercase tracking-widest text-muted transition-colors hover:border-accent hover:text-accent focus-visible:border-accent focus-visible:text-accent focus-visible:outline-none"
            >
              {showAll ? "Vis færre publikasjoner" : `Vis alle publikasjoner (${hiddenCount} til)`}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function YearGroup({ year, items }: { year: number; items: Publication[] }) {
  return (
    <li>
      <h3 className="bg-bg/90 sticky top-16 z-10 mb-2 flex items-baseline justify-between gap-4 border-b border-line py-2 font-mono text-xs uppercase tracking-widest text-muted backdrop-blur">
        <span className="text-fg/80 font-display text-base font-normal">{year}</span>
        <span>
          {items.length} {items.length === 1 ? "publikasjon" : "publikasjoner"}
        </span>
      </h3>
      <ul className="divide-line/60 divide-y">
        {items.map((p) => (
          <PublicationItem key={p.id} pub={p} />
        ))}
      </ul>
    </li>
  );
}

function PublicationItem({ pub }: { pub: Publication }) {
  return (
    <li className="group py-2.5">
      <a
        href={pub.url ?? "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus-visible:outline-none md:grid md:grid-cols-[1fr_auto_5.5rem] md:items-baseline md:gap-x-6 md:gap-y-1"
      >
        <h4 className="font-display text-base font-normal leading-snug text-fg group-hover:text-accent md:text-lg">
          {pub.title}
        </h4>
        <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted md:mt-0 md:text-[0.7rem]">
          {pub.venue ?? venueTypeLabel(pub.venue_type)}
        </p>
        <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted md:mt-0 md:text-right md:text-[0.7rem]">
          {citationText(pub.citations)}
        </p>
      </a>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ScholarlyArticle",
            name: pub.title,
            author: pub.authors.map((name) => ({ "@type": "Person", name })),
            datePublished: `${pub.year}`,
            isPartOf: pub.venue ? { "@type": "Periodical", name: pub.venue } : undefined,
            identifier: pub.doi ? `https://doi.org/${pub.doi}` : pub.arxiv_id,
            url: pub.url ?? undefined,
          }),
        }}
      />
    </li>
  );
}

function venueTypeLabel(type: Publication["venue_type"]) {
  const labels: Partial<Record<Publication["venue_type"], string>> = {
    journal: "Tidsskrift",
    conference: "Konferanse",
    workshop: "Workshop",
    preprint: "Preprint",
    book_chapter: "Bokkapittel",
    thesis: "Avhandling",
    other: "Publikasjon",
  };
  return labels[type] ?? "Publikasjon";
}

function citationText(citations: number | null) {
  if (citations === null || citations <= 0) return "—";
  return `${citations} ${citations === 1 ? "sitering" : "siteringer"}`;
}
